<template>
	<div class="bg5 p2">
		<div class="xs-12 flex wrap xs-between">
			<div class="fw7 fs4 c6 lh125">{{ productName }}</div>
		</div>
		<!-- header -->
		<div class="xs-12 mt6 flex">
			<div class="xs-6 fs3 fw7">{{ t("key") }}</div>
			<div class="xs-3 fs3 fw7">{{ t("rate") }} %</div>
			<div class="xs-3 fs3 fw7">{{ t("value") }}</div>
		</div>
		<!-- list -->
		<div class="mt2 mb6">
			<div
				v-for="(value, name) in values"
				:key="name"
				class="flex xs-12 pb1 xs-middle"
			>
				<div class="xs-6 fs3">{{ label[name] }}</div>

				<!-- value -->
				<div class="xs-3 pr1">
					<core-form-input
						componentName="j-base-input-number"
						:name="`${name}-value`"
						type="float"
						size="small"
						v-model="value.rate"
						v-validate="'required'"
						:validationErrors="errors"
						aria-autocomplete="none"
						@input="debouncedFetchPrice"
					/>
				</div>
				<!-- rate -->
				<div class="xs-3">
					<core-form-input
						componentName="j-base-input-number"
						:name="`${name}-rate`"
						type="float"
						size="small"
						v-validate="'required'"
						v-model="value.value"
						:validationErrors="errors"
						aria-autocomplete="none"
						@input="debouncedFetchPrice"
					/>
				</div>
			</div>
		</div>

		<div
			class="mb3 flex xs-around xs-middle"
			v-for="(item, i) in span"
			:key="i"
		>
			<div class="xs-6">
				<span class="fw7">{{ item.name }}:</span>
			</div>
			<div class="xs-6 flex xs-right">
				<span class="fw7">{{ item.value }}</span>
			</div>
		</div>
		<div class="flex xs-around xs-middle">
			<div class="xs-6">
				<span class="fw7">{{ t("total_amount") }}:</span>
			</div>
			<div class="xs-6 flex xs-right">
				<span class="fw7">{{ computedPrice }}</span>
			</div>
		</div>
		<!-- Save value -->
		<j-button class="mt6" size="small" @click="saveValues">{{
			t("save_values")
		}}</j-button>
	</div>
</template>

<script>
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import debounce from "lodash.debounce"

import JButton from "@/components/JButton"
import CoreFormInput from "@/modules/core/components/CoreFormInput"

const presetValues = {
	transit_stock: {
		rate: 0,
		value: 0
	},
	transit_money: {
		rate: 0,
		value: 0
	},
	swallows_nest_preserved_seafood: {
		rate: 0,
		value: 0
	},
	sundry_goods_wine_spirit: {
		rate: 0,
		value: 0
	},
	cigar_cigarettes_tobacco_pipe_related_goods: {
		rate: 0,
		value: 0
	},
	premises_money: {
		rate: 0,
		value: 0
	},
	furniture_fixture_fitting: {
		rate: 0,
		value: 0
	}
}

export default {
	props: {
		productId: {
			validator(value) {
				return typeof value === "object"
			}
		},
		caseId: String,
		proposalId: String,
		offerId: String
	},

	components: {
		CoreFormInput,
		JButton
	},

	data() {
		return {
			productName: "",
			price: null,

			label: {
				transit_stock: "Transit Stock",
				transit_money: "Transit Money",
				swallows_nest_preserved_seafood: "Swallows Nest/Preserved Seafood",
				sundry_goods_wine_spirit: "Sundry Goods/Wine Spirit",
				cigar_cigarettes_tobacco_pipe_related_goods:
					"Cigar/Cigarettes/Tobacco/Pipe Related Goods",
				premises_money: "Premises Money",
				furniture_fixture_fitting: "F.F.F"
			},
			values: null,
			isDisabled: null,
			appliedTaxes: null
		}
	},

	computed: {
		computedValues() {
			return this.values
		},
		computedPrice() {
			return this.price
		},
		computedTaxes() {
			return this.appliedTaxes
		},
		plan() {
			return this.productId?.plan
		}
	},

	methods: {
		t(key) {
			const locale = {
				"en-US": {
					key: "Key",
					rate: "Rate",
					save_values: "Save Values",
					total_amount: "Total Amount",
					retail: "Retail",
					wholesale: "Wholesale",
					value: "Value"
				},
				"zh-HK": {}
			}
			return locale[this.$store.state.settings.locale][key]
		},

		applyPresetValues() {
			// deep clone so that original presetValues is not mutated
			this.values = JSON.parse(JSON.stringify(presetValues))
		},

		async fetchPrice() {
			const isValid = await this.$validator.validate()
			if (!isValid) return

			const query = gql`
				query getPrice($where: productWhereInput, $values: [keyValueInput]) {
					products: products_2(where: $where, values: $values) {
						list {
							name
							productId {
								plan
							}
							pricing(values: $values) {
								appliedTaxes {
									name
									code
									ratio
									formattedFlat
								}
								formattedPrice
							}
						}
					}
				}
			`

			const res = await api({
				query,
				variables: {
					where: {
						productId: {
							plan: this.plan
						}
					},
					...this.massageValuesForAPI()
				}
			})

			this.productName = res?.data?.products?.list?.[0]?.name
			this.price = res?.data?.products?.list?.[0]?.pricing?.formattedPrice
			this.appliedTaxes = res?.data?.products?.list?.[0]?.pricing?.appliedTaxes.map(
				item => {
					return {
						name: item.name ? item.name : item.code,
						value: this.getValue(item).flat
					}
				}
			)
		},

		debouncedFetchPrice: debounce(function() {
			this.fetchPrice()
		}, 500),

		async fetchValues() {
			const query = gql`
				fragment ObjectValue on scalarValue {
					objectValue {
						key
						value {
							objectValue {
								key
								value {
									numberValue
								}
							}
						}
					}
				}

				query checkPolicyValues(
					$caseWhere1: caseWhere
					$offerWhere1: offerWhere
				) {
					cases(where: $caseWhere1) {
						list {
							id
							proposals {
								basket(where: $offerWhere1) {
									id
									values {
										key
										value {
											arrayValue {
												...ObjectValue
											}
										}
									}
								}
							}
						}
					}
				}
			`

			const res = await api({
				query,
				variables: {
					caseWhere1: { id: this.caseId },
					offerWhere1: { id: this.offerId }
				}
			})

			// if offer has no values, we apply preset values
			if (!res?.data?.cases?.list?.[0]?.proposals?.[0]?.basket?.[0]?.values) {
				this.isDisabled = false
				this.applyPresetValues()
				return
			}

			this.isDisabled = true

			this.values = this.massageValuesFromAPI(
				res?.data?.cases?.list?.[0]?.proposals?.[0]?.basket?.[0]?.values
			)
		},

		async saveValues() {
			const query = gql`
				mutation updateOfferOfProposal(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$updateOfferInput: updateOfferInput!
				) {
					updateOfferOfProposal(
						caseId: $caseId
						offerId: $offerId
						proposalId: $proposalId
						updateOfferInput: $updateOfferInput
					) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`
			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				updateOfferInput: this.massageValuesForAPI()
			}

			const res = await api({
				query,
				variables
			})

			if (!res?.data?.updateOfferOfProposal?.errors)
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("success")
					}
				})
			this.isDisabled = true
			this.$emit("premium:updated")
		},

		massageValuesFromAPI(values) {
			return values
				?.find(item => item.key === "insureds")
				?.value?.arrayValue?.[0]?.objectValue?.reduce((obj, item) => {
					let key = item?.key
					let value = item?.value?.objectValue.reduce((innerObj, innerItem) => {
						let innerKey = innerItem?.key
						let innerValue =
							innerKey === "rate"
								? parseFloat((innerItem?.value?.numberValue * 100).toFixed(6))
								: innerItem?.value?.numberValue
						innerObj[innerKey] = innerValue
						return innerObj
					}, {})
					obj[key] = value
					return obj
				}, {})
		},

		massageValuesForAPI() {
			return {
				values: [
					{
						key: "insureds",
						value: {
							arrayValue: [
								{
									objectValue: Object.keys(this.computedValues).map(key => {
										return {
											key,
											value: {
												objectValue: [
													{
														key: "rate",
														value: {
															numberValue: this.computedValues[key].rate / 100
														}
													},
													{
														key: "value",
														value: {
															numberValue: this.computedValues[key].value
														}
													}
												]
											}
										}
									})
								}
							]
						}
					}
				]
			}
		},

		getValue(item) {
			if (!item) return ""
			const ratio = item.ratio ? item.ratio * 100 : null
			const flat = item.formattedFlat ? item.formattedFlat : "0"
			return {
				flat,
				summary: ratio
					? `(${ratio ? ratio.toFixed(2) : "0"}% of ${
							item.formattedOriginalPrice
					  })`
					: ""
			}
		}
	},

	async created() {
		await this.fetchValues()
		await this.fetchPrice()
	},

	watch: {
		// When productId updates, refetch data
		// This is need because we use same component for different productId
		// And thus we have to refetch as AegPricing doesn't remount the component
		productId: {
			handler() {
				this.fetchPrice()
			}
		},
		// when offerId changes,
		// and refetch values
		offerId: async function() {
			await this.fetchValues()
		}
	}
}
</script>
